import type { ReactNode } from 'react'

// Leaf modules only: this file ships in the app's first chunk, and the
// components/ui barrel would bring Radix Select, Tooltip and Dialog with it.
import { Mail } from '@/components/icons'
import { Button } from '@/components/ui/Button'

import { GateFrame } from './GateFrame'
import { GoogleIcon } from './GoogleIcon'

export const SIGN_IN_KICKER = 'Agent dashboard'
export const SIGN_IN_LEDE = 'Sign in to talk with your agent and keep its lists, files and settings in order.'

/** The sign-in page's h1: the page's Largest Contentful Paint. */
export function SignInTitle() {
  return <>Your fire<br />is still <span className="text-[color:var(--bl-gold)]">lit.</span></>
}

export function CardTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-5 space-y-1">
      <h2 className="font-[family-name:var(--bl-font-display)] text-2xl font-bold uppercase tracking-[0.03em] text-[color:var(--bl-ink)]">{children}</h2>
      {hint ? <p className="text-sm leading-relaxed text-[color:var(--bl-ink-2)]">{hint}</p> : null}
    </div>
  )
}

function PhoneIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  )
}

interface SignInOptionsProps {
  /** The session is still being checked: the options hold their place, hidden, under a loading line. */
  pending?: boolean
  onGoogle?: () => void
  onEmail?: () => void
  onPhone?: () => void
}

/** The sign-in card's first view: Google, email, or linking an agent's phone. */
export function SignInOptions({ pending = false, onGoogle, onEmail, onPhone }: SignInOptionsProps) {
  return (
    <div className="space-y-4">
      <CardTitle>Sign in</CardTitle>
      <div className="relative">
        <div aria-hidden={pending || undefined} className={pending ? 'invisible space-y-4' : 'space-y-4'}>
          <Button variant="secondary" size="lg" fullWidth onClick={onGoogle} leftIcon={<GoogleIcon />}>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-[color:var(--bl-line)]" />
            <span className="bl-label">or</span>
            <div className="h-px flex-1 bg-[color:var(--bl-line)]" />
          </div>

          <Button variant="outline" size="lg" fullWidth onClick={onEmail} leftIcon={<Mail size={16} aria-hidden="true" />}>
            Sign in with email
          </Button>

          <div className="pt-1 text-center">
            <button
              type="button"
              onClick={onPhone}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--bl-ink-3)] transition-colors hover:text-[color:var(--bl-gold-text)]"
            >
              <PhoneIcon />
              Have an agent? Link your account
            </button>
          </div>
        </div>
        {pending ? (
          <p role="status" className="absolute inset-0 flex items-center justify-center gap-3 text-sm text-[color:var(--bl-ink-3)]">
            <span aria-hidden="true" className="bl-ember-spinner" />
            Loading...
          </p>
        ) : null}
      </div>
    </div>
  )
}

/** Under every view of the sign-in card. */
export function SignUpNote() {
  return (
    <p className="mt-6 border-t border-[color:var(--bl-line)] pt-5 text-center text-xs text-[color:var(--bl-ink-3)]">
      Don't have an agent?{' '}
      <a href="https://agent.badland.ai" className="font-semibold text-[color:var(--bl-gold-text)] hover:underline">
        Sign up
      </a>
    </p>
  )
}

/**
 * The sign-in gate while the session is checked, before LoginPage and the
 * ember scene have loaded. index.html paints this exact markup before the app
 * does (its title is the page's Largest Contentful Paint; GateShell.test.tsx
 * keeps the two identical), AuthGate keeps it up while `auth.me` answers, and
 * the login route shows it while its page loads. LoginPage then draws the same
 * frame, title and card with the live scene and working buttons.
 */
export function GateShell() {
  return (
    <GateFrame
      mode="shell"
      scene={<div aria-hidden="true" className="bl-backdrop" />}
      kicker={SIGN_IN_KICKER}
      title={<SignInTitle />}
      lede={SIGN_IN_LEDE}
    >
      <SignInOptions pending />
      <SignUpNote />
    </GateFrame>
  )
}
